import type { ThemeOptions } from '@mui/material'
import {
  iconButtonClasses,
  listClasses,
  listItemAvatarClasses,
  listItemClasses,
  listItemSecondaryActionClasses,
  listItemTextClasses,
} from '@mui/material'

import { PhxIcon, createConfiguratorTheme } from '@salsita-ai/platform-project-sdk/ui'

const themeOptions = {
  components: {
    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
      },
      styleOverrides: {
        root: {
          // Disable accordion divider
          '&::before': {
            content: 'none',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: ({ theme: { palette, spacing } }) => ({
          padding: 0,

          [`.${listClasses.root}`]: {
            display: 'grid',
            gap: spacing(0.5),
            paddingBlock: spacing(1.5),
          },
          [`.${listItemClasses.root}`]: {
            gap: spacing(1.5),

            '&[class]': {
              padding: `${spacing(0.5)} ${spacing(1)}`,
            },
          },
          [`.${listItemAvatarClasses.root}`]: {
            minWidth: 0,
          },
          [`.${listItemTextClasses.root}`]: {
            margin: 0,
          },
          [`.${listItemSecondaryActionClasses.root}`]: {
            '&[class][class]': {
              padding: 0,
            },
            [`.${iconButtonClasses.root}`]: {
              blockSize: 36,
              color: palette.action.active,
              inlineSize: 36,
            },
          },
        }),
      },
    },
    MuiAccordionSummary: {
      defaultProps: {
        expandIcon: <PhxIcon name="CaretDown" fontSize="small" />,
      },
      styleOverrides: {
        root: ({ theme: { spacing } }) => ({
          paddingInline: `${spacing(1)} ${spacing(2)}`,

          // Remove empty `PropertyContainerContent` margin
          '& :empty': {
            margin: 0,
          },
        }),
        content: {
          margin: 0,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          variants: [
            {
              props: { variant: 'contained', color: 'primary' },
              style: {
                '&:is(:focus-visible, :hover)': {
                  backgroundColor: palette.primary.light,
                },
              },
            },
          ],
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          maxWidth: 648,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          color: palette.primary.main,
        }),
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
    },
    PhxBrandingHeader: {
      defaultProps: {
        imageHeight: 60,
        imageUrl: './images/lynx-logo-small.png',
      },
    },
    PhxCardSelectorItem: {
      styleOverrides: {
        header: {
          blockSize: 72,
          padding: 0,
        },
        image: {
          blockSize: 70,
          objectFit: 'cover',
        },
      },
    },
    PhxDeleteEntityButton: {
      defaultProps: {
        muted: true,
      },
    },
    PhxDeleteEntityFab: {
      defaultProps: {
        muted: true,
      },
    },
    PhxDesktopToolbarButton: {
      styleOverrides: {
        button: ({ theme: { palette } }) => ({
          color: palette.primary.main,
        }),
      },
    },
    PhxNumericalInput: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    PhxDesktopBrandingPoweredBy: {
      defaultProps: {
        visible: true,
      },
    },
    PhxPropertyContainerItem: {
      styleOverrides: {
        content: ({ theme: { spacing } }) => ({
          marginBlockStart: spacing(2),
        }),
      },
    },
    PhxSidebarPanel: {
      styleOverrides: {
        padding: {
          padding: 20,
        },
      },
    },
    PhxToggleSelectorRoot: {
      defaultProps: {
        columnCount: 'auto-fit',
      },
    },
    PhxToggleSelectorItem: {
      styleOverrides: {
        button: ({ theme: { spacing } }) => ({
          paddingBlock: spacing(1),
        }),
      },
    },
    PhxTopNavigation: {
      styleOverrides: {
        root: () => ({
          justifyContent: 'center',
        }),
        stepper: {
          maxWidth: 856,
        },
      },
    },
  },

  // ---------------------------------------------------------------------------
  palette: {
    background: {
      default: '#F1F1F1',
      scene: '#F3F2EF',
    },

    primary: {
      main: '#0F0F0F',
    },
  },

  // ---------------------------------------------------------------------------
  shape: {
    borderRadius: 0,
  },

  // ---------------------------------------------------------------------------
  typography: {
    fontFamily: '"Urbanist Variable", sans-serif',

    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    body2: {
      letterSpacing: '0.012143em',
    },
    subtitle2: {
      letterSpacing: '0.007143em',
    },
    overline: {
      letterSpacing: '0.083333em',
    },
    caption: {
      fontWeight: 600,
    },
  },
} satisfies ThemeOptions

export const jmsTheme = createConfiguratorTheme(themeOptions)
